import React, { useLayoutEffect, useState } from 'react';
import { ContentTile, CtaCard, StatCard } from 'components/molecules';
import PropTypes, { any } from 'prop-types';
import { getLinkProps } from 'utils/get-link-props';
import { eventTrack, LINK_LIST_CTA_CLICK } from 'utils/google-tag-manager';
import { Button, Paragraph } from 'components/atoms';
import { get, kebabCase } from 'lodash';
import { useOverlay } from 'queries/overlay';
import { useCarouselScroll } from 'components/molecules/audio-card-section/useCarouselScroll';
import * as Styled from './TileCarousel.styled';
import Overlay from '../overlay/Overlay';

const onClickCta = event => eventTrack(LINK_LIST_CTA_CLICK, { event });

/**
 * Renders the Tile Carousel
 * @param {string} id
 * @param {string} title
 * @param {string} titleColor
 * @param {string} description
 * @param {object} cta
 * @param {Array} tiles
 * @param {boolean} scrollHorizontal
 * @param {boolean} scrollHorizontalMobile
 * @param {boolean} fullWidth
 * @param {boolean} isArticleMargin
 * @returns {ReactElement}
 */
const TileCarousel = ({
  id,
  title = '',
  titleColor = '',
  description = '',
  cta = null,
  tiles = [],
  scrollHorizontal = true,
  scrollHorizontalMobile = true,
  fullWidth = false,
  isArticleMargin = false,
}) => {
  const overlays = tiles.map(tile => tile.trendInfo?.sys?.id || null);
  const [currentOverlay, setCurrentOverlay] = useState(0);

  const {
    showOverlay,
    overlayData,
    handleOverlayOpen,
    handleOverlayClose,
  } = useOverlay();

  const {
    rowRef,
    canScrollLeft,
    canScrollRight,
    scrollToDirection,
    checkScrollState,
    isScrollInitialized,
  } = useCarouselScroll();

  useLayoutEffect(() => {
    checkScrollState();
  }, [tiles, checkScrollState]);

  const handleSetOverlay = index => {
    setCurrentOverlay(index);
    if (tiles[index]?.trendInfo?.sys?.id) {
      handleOverlayOpen(tiles[index].trendInfo.sys.id);
    }
  };

  const getNextOverlayIndex = index => {
    let currIndex = index + 1;
    while (currIndex < overlays.length) {
      if (overlays[currIndex]) {
        return currIndex;
      }
      currIndex += 1;
    }
    return null;
  };

  const getPreviousOverlayIndex = index => {
    let currIndex = index - 1;
    while (currIndex >= 0) {
      if (overlays[currIndex]) {
        return currIndex;
      }
      currIndex -= 1;
    }
    return null;
  };

  const renderTile = (tile, index) => {
    // eslint-disable-next-line no-underscore-dangle
    switch (tile.__typename) {
      case 'StatCard':
        return (
          <StatCard
            key={kebabCase(`${tile.title}-${index}`)}
            title={tile.title}
            subtitle={tile.subtitle}
            captionTitle={tile.captionTitle}
            caption={tile.caption}
            textColor={tile.textColor}
            backgroundColor={tile.backgroundColor}
            image={tile.image}
            themeSelector={tile.themeSelector}
            className="carousel-card"
          />
        );
      case 'CtaCard':
        return (
          <CtaCard
            key={kebabCase(`${tile.title}-${index}`)}
            title={tile.title}
            description={tile.description}
            image={tile.image}
            ctas={get(tile, 'ctasCollection.items', [])}
            themeSelector={tile.themeSelector}
            className="carousel-card"
          />
        );
      default:
        return (
          <ContentTile
            tile={tile}
            className="carousel-card"
            handleClick={() => {
              if (tile.trendInfo?.sys?.id || tile.themeSelector === 'overlay') {
                handleSetOverlay(index);
              }
            }}
            key={kebabCase(`${tile.title}-${index}`)}
          />
        );
    }
  };

  return (
    <Styled.TileCarousel
      id={id}
      fullWidth={fullWidth}
      isArticleMargin={isArticleMargin}
    >
      <Styled.Container fullWidth={fullWidth} isArticleMargin={isArticleMargin}>
        {title && (
          <Styled.TitleContainer $titleColor={titleColor}>
            <Styled.Title styling="h1" tag="h2" text={title} />
            <Styled.DescriptionContainer hasDescription={description}>
              {description && <Paragraph styling="P1">{description}</Paragraph>}
              {cta && (
                <Styled.Cta
                  type={cta.type ? cta.type : 'secondary'}
                  isDarkTheme
                  overrideFunctionality={cta.overrideFunctionality}
                  {...getLinkProps(cta.url)}
                  onClick={onClickCta}
                  hasDescription={description}
                >
                  {cta.title}
                </Styled.Cta>
              )}
            </Styled.DescriptionContainer>
          </Styled.TitleContainer>
        )}
        <Styled.RowContainer>
          <Styled.TileContainer
            ref={rowRef}
            $scrollHorizontal={scrollHorizontal}
            $scrollHorizontalMobile={scrollHorizontalMobile}
            $isArticleMargin={isArticleMargin}
            $length={tiles.length}
          >
            {tiles.map((tile, index) => renderTile(tile, index))}
          </Styled.TileContainer>
          <Styled.ButtonContainer
            $isVisible={canScrollLeft && scrollHorizontal}
            $isInitialized={isScrollInitialized}
            $titleColor={titleColor}
            $direction="left"
          >
            <Button type="roundLeftArrow" onClick={scrollToDirection('left')} />
          </Styled.ButtonContainer>
          <Styled.ButtonContainer
            $isVisible={canScrollRight && scrollHorizontal}
            $isInitialized={isScrollInitialized}
            $titleColor={titleColor}
            $direction="right"
          >
            <Button
              type="roundRightArrow"
              onClick={scrollToDirection('right')}
            />
          </Styled.ButtonContainer>
        </Styled.RowContainer>
      </Styled.Container>
      <Overlay
        data={overlayData}
        handleClose={handleOverlayClose}
        isOpen={showOverlay && overlayData}
        previousTitle={
          getPreviousOverlayIndex(currentOverlay) !== null
            ? tiles[getPreviousOverlayIndex(currentOverlay)].title
            : undefined
        }
        handlePrevious={() => {
          const prevIndex = getPreviousOverlayIndex(currentOverlay);
          if (prevIndex !== null) {
            handleSetOverlay(prevIndex);
          }
        }}
        nextTitle={
          getNextOverlayIndex(currentOverlay) !== null
            ? tiles[getNextOverlayIndex(currentOverlay)].title
            : undefined
        }
        handleNext={() => {
          const nextIndex = getNextOverlayIndex(currentOverlay);
          if (nextIndex !== null) {
            handleSetOverlay(nextIndex);
          }
        }}
      />
    </Styled.TileCarousel>
  );
};

TileCarousel.propTypes = {
  /**
   * The id of the component
   */
  id: PropTypes.string,
  /**
   * The title of the component
   */
  title: PropTypes.string,
  /**
   * The description of the component
   */
  description: PropTypes.string,
  /**
   * The cta
   */
  cta: PropTypes.objectOf(any),
  /**
   * List of content tiles
   */
  tiles: PropTypes.arrayOf(any),
  /**
   * true if we want to scroll horizontally on desktop
   */
  scrollHorizontal: PropTypes.bool,
  /**
   * true if we want to scroll horizontally on mobile
   */
  scrollHorizontalMobile: PropTypes.bool,
  /**
   * true if we want to remove padding on sides
   */
  fullWidth: PropTypes.bool,
};

export default TileCarousel;
